/**
 * threadNotifications — the notification mode a person hears messages by
 * (owner directive 2026-08-26; ONE SETTING PER PERSON since 2026-09-17).
 *
 * The chat header carries a bell that cycles THREE states — one setting for all
 * of this person's threads (Miles: "it stays on that setting per user, not per
 * thread"), edited from the bell or the Settings page, whichever they reach:
 *   1. 'chime-platform' — a chime here and a notification on every device they
 *                         are signed in on, for every message;
 *   2. 'chime'          — ONLY messages that are FOR them: someone named them,
 *                         replied to them, Prest0n answered their question, or
 *                         they are the only other person in the thread. THE
 *                         PLATFORM DEFAULT. Until 2026-09-17 this state chimed
 *                         for every message on this side while the server had
 *                         always treated it as mentions-only — its own tooltip
 *                         advertised "quick chime on new messages", which is
 *                         the string Miles screenshotted;
 *   3. 'off'            — silent, in every thread, mentions included.
 *
 * The choice is the VIEWER's alone ("not everyone in the group thread").
 *
 * `resolveNotifyMode` is the ladder and `arrivalIsAddressedToViewer` is the
 * "was that for me?" predicate; both are twins of the server's
 * (functions/src/notifications/threadNotifyMode.ts and the fan-out's own rule),
 * pinned by a parity table rather than shared by an import, since `src/` cannot
 * import from `functions/`.
 *
 * The per-thread DEFAULTS below survive for the email and text channels, which
 * the dispatch plane delivers separately and which the bell never governed.
 *
 * Email/text delivery is a server concern; the defaults persist alongside the
 * other notification channel preferences (users/{uid}/user_settings) for the
 * dispatch plane to consume. This module owns everything the BROWSER does:
 * mode storage/resolution, the cycle, the chime, and the platform
 * notification.
 *
 * Pure state + storage helpers, no React — unit-tested directly.
 */

import { MESSAGING_SW_SCOPE } from './notificationCapability';
import { buildThreadDeepLinkUrl, requestOpenThreadUrl } from '../lib/threadDeepLink';
import { BRAND_NOTIFICATION_BADGE, BRAND_NOTIFICATION_ICON } from '../config/brandMark.generated';

export type ThreadNotifyMode = 'chime' | 'chime-platform' | 'off';

/** One defaults row: the chime plus the three server-delivered channels. */
export interface ThreadNotificationDefaultRow {
  chime: boolean;
  platform: boolean;
  email: boolean;
  text: boolean;
}

export interface ThreadNotificationDefaults {
  thread: ThreadNotificationDefaultRow;
  groupThread: ThreadNotificationDefaultRow;
}

/** The stated factory defaults: chime on, every other channel off, both kinds. */
export const FACTORY_THREAD_NOTIFICATION_DEFAULTS: ThreadNotificationDefaults = {
  thread: { chime: true, platform: false, email: false, text: false },
  groupThread: { chime: true, platform: false, email: false, text: false },
};

const DEFAULTS_STORAGE_KEY = 'threadNotificationDefaults';

/**
 * Per-USER defaults key (2026-08-27 review fix): the unscoped key leaked one
 * account's defaults into the next on a shared browser — and because the
 * per-thread stamp freezes whatever defaults it read, the leak was permanent
 * for every thread first encountered under the wrong identity. The unscoped
 * key remains the read fallback (and the write target when no uid is known),
 * so pre-fix stored defaults keep working.
 */
function defaultsStorageKey(uid?: string | null): string {
  const id = (uid ?? '').trim();
  return id ? `${DEFAULTS_STORAGE_KEY}:v1:${id}` : DEFAULTS_STORAGE_KEY;
}

function sanitizeRow(raw: unknown, fallback: ThreadNotificationDefaultRow): ThreadNotificationDefaultRow {
  if (!raw || typeof raw !== 'object') return { ...fallback };
  const rec = raw as Record<string, unknown>;
  return {
    chime: typeof rec.chime === 'boolean' ? rec.chime : fallback.chime,
    platform: typeof rec.platform === 'boolean' ? rec.platform : fallback.platform,
    email: typeof rec.email === 'boolean' ? rec.email : fallback.email,
    text: typeof rec.text === 'boolean' ? rec.text : fallback.text,
  };
}

export function loadThreadNotificationDefaults(uid?: string | null): ThreadNotificationDefaults {
  try {
    const raw = typeof localStorage !== 'undefined'
      ? (localStorage.getItem(defaultsStorageKey(uid))
          ?? localStorage.getItem(DEFAULTS_STORAGE_KEY))
      : null;
    if (!raw) return FACTORY_THREAD_NOTIFICATION_DEFAULTS;
    const parsed = JSON.parse(raw) as Record<string, unknown>;
    return {
      thread: sanitizeRow(parsed.thread, FACTORY_THREAD_NOTIFICATION_DEFAULTS.thread),
      groupThread: sanitizeRow(parsed.groupThread, FACTORY_THREAD_NOTIFICATION_DEFAULTS.groupThread),
    };
  } catch {
    return FACTORY_THREAD_NOTIFICATION_DEFAULTS;
  }
}

export function saveThreadNotificationDefaults(
  defaults: ThreadNotificationDefaults,
  uid?: string | null,
): void {
  try {
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem(defaultsStorageKey(uid), JSON.stringify(defaults));
    }
  } catch { /* preference degrades to factory defaults on this device */ }
}

/**
 * Map a defaults row onto the bell's three-state mode. Platform-on implies the
 * chime (that is what the cycle's second state means); chime alone is the
 * first state; neither is off.
 */
export function modeFromDefaults(defaults: ThreadNotificationDefaults, isGroup: boolean): ThreadNotifyMode {
  const row = isGroup ? defaults.groupThread : defaults.thread;
  if (row.platform) return 'chime-platform';
  if (row.chime) return 'chime';
  return 'off';
}

/**
 * The bell's click order — DESCENDING loudness since 2026-09-17:
 * everything → mentions only → off → everything → …
 *
 * It used to run mentions → everything → off, which read as "quiet, louder,
 * silent". Loud-to-quiet matches what the three glyphs show and what someone
 * clicking a bell is almost always trying to do.
 */
export function cycleThreadNotifyMode(mode: ThreadNotifyMode): ThreadNotifyMode {
  switch (mode) {
    case 'chime-platform': return 'chime';
    case 'chime': return 'off';
    default: return 'chime-platform';
  }
}

export function isThreadNotifyMode(raw: unknown): raw is ThreadNotifyMode {
  return raw === 'chime' || raw === 'chime-platform' || raw === 'off';
}

// ─── ONE SETTING PER PERSON (Miles, 2026-09-17) ──────────────────────────────
//
// What used to live here — `resolveThreadNotifyMode`, which stamped the current
// defaults into a PER-THREAD localStorage key on first encounter of every
// thread, per device — is deleted. That stamp was the mechanism that made "per
// user" impossible: it deliberately froze whatever the defaults said at the
// moment a thread was first opened, so no later default could ever reach a
// thread anyone had already looked at. Its removal IS the requirement.
//
// `getStoredThreadNotifyMode` / `setStoredThreadNotifyMode` / the per-thread key
// go with it. Any key a browser still holds is simply never read again.

/** The person's own account-wide mode, mirrored for an instant first paint. */
const USER_MODE_STORAGE_KEY = 'prest0n:notify_mode:v1';

function userModeStorageKey(uid: string): string {
  return `${USER_MODE_STORAGE_KEY}:${uid}`;
}

/**
 * The mode this device last saw for this person, or null.
 *
 * A mirror, never the authority: the value lives in Firestore at
 * `users/{uid}/chat_threads_meta/notifications.mode` so it follows them across
 * devices. This exists so the bell renders the right glyph on the first frame
 * instead of flickering through a default.
 */
export function readCachedUserNotifyMode(uid: string): ThreadNotifyMode | null {
  if (!uid) return null;
  try {
    const raw = typeof localStorage !== 'undefined' ? localStorage.getItem(userModeStorageKey(uid)) : null;
    return isThreadNotifyMode(raw) ? raw : null;
  } catch {
    return null;
  }
}

export function cacheUserNotifyMode(uid: string, mode: ThreadNotifyMode): void {
  if (!uid) return;
  try {
    if (typeof localStorage !== 'undefined') localStorage.setItem(userModeStorageKey(uid), mode);
  } catch { /* the in-memory state still holds for this session */ }
}

/**
 * THE PLATFORM DEFAULT — the client's half of the 2026-09-17 flip.
 *
 * Kept in lockstep with the server's `PLATFORM_DEFAULT_NOTIFY_MODE`
 * (functions/src/notifications/threadNotifyMode.ts). `src/` cannot import from
 * `functions/`, so the two are twins pinned by a parity test rather than one
 * import — the same arrangement the mention resolvers already use.
 *
 * `REACT_APP_THREAD_NOTIFY_DEFAULT=everything` is the client-side rollback; the
 * server's own lever is `ops/threadNotify.defaultMode`.
 */
export const PLATFORM_DEFAULT_NOTIFY_MODE: ThreadNotifyMode = 'chime';

export function platformDefaultNotifyMode(): ThreadNotifyMode {
  const override = String(process.env.REACT_APP_THREAD_NOTIFY_DEFAULT || '').trim().toLowerCase();
  if (override === 'everything' || override === 'chime-platform') return 'chime-platform';
  if (override === 'off') return 'off';
  if (override === 'mentions' || override === 'chime') return 'chime';
  return PLATFORM_DEFAULT_NOTIFY_MODE;
}

/**
 * The effective mode for this person. The client's twin of the server's
 * `resolveEffectiveNotifyMode`, and the same ladder in the same order:
 *
 *   1. their own account-wide choice;
 *   2. a per-thread stamp the server left as a starting position (the founder
 *      platform-thread defaults) — still honoured for somebody who has never
 *      chosen, which is what lets this ship with no backfill;
 *   3. an explicit legacy platform-notification opt-in from their Settings;
 *   4. the platform default.
 *
 * `archived` is not an input: it silences a thread before any mode question.
 */
export function resolveNotifyMode(args: {
  perUser?: ThreadNotifyMode | null;
  perThread?: ThreadNotifyMode | null;
  legacyPlatformOptIn?: boolean;
}): ThreadNotifyMode {
  if (isThreadNotifyMode(args.perUser)) return args.perUser;
  if (isThreadNotifyMode(args.perThread)) return args.perThread;
  if (args.legacyPlatformOptIn === true) return 'chime-platform';
  return platformDefaultNotifyMode();
}

/**
 * Is this arrival FOR this person? The client's half of the server's
 * "addressed" rule, decided from the index doc alone — which is all the rail
 * watcher has, since it never sees message text.
 *
 * Three ways, matching the server exactly:
 *   • the server stamped `lastAddressedAt` at or after this activity (someone
 *     named them, replied to them, or Prest0n answered their question);
 *   • they are the ONLY other person in the thread, so every message in it was
 *     written to them (a 1:1);
 *   • the thread is their own — nobody else is in it, so a reply is an answer
 *     to them by construction.
 */
export function arrivalIsAddressedToViewer(args: {
  lastActiveAt?: string | null;
  lastAddressedAt?: string | null;
  /** Distinct humans in the thread, viewer included. Unknown ⇒ undefined. */
  participantCount?: number | null;
  sharedThread?: boolean;
}): boolean {
  if (args.sharedThread !== true) return true;              // their own thread
  const people = typeof args.participantCount === 'number' ? args.participantCount : null;
  if (people !== null && people <= 2) return true;          // a 1:1
  const addressed = typeof args.lastAddressedAt === 'string' ? args.lastAddressedAt : '';
  if (!addressed) return false;
  const active = typeof args.lastActiveAt === 'string' ? args.lastActiveAt : '';
  return !active || addressed >= active;                    // ISO compares chronologically
}

// ─── The chime ───────────────────────────────────────────────────────────────

let audioContext: AudioContext | null = null;
let lastChimeAt = 0;
/** Never chime more often than this — a burst of arrivals is ONE event. */
export const CHIME_MIN_INTERVAL_MS = 1_500;

/**
 * A quick two-tone chime, synthesized (no asset to load or cache-bust).
 * Quiet by design; throttled so a flurry of messages reads as one arrival.
 */
export function playThreadChime(now: number = Date.now()): boolean {
  if (now - lastChimeAt < CHIME_MIN_INTERVAL_MS) return false;
  try {
    const Ctor = (window as unknown as { AudioContext?: typeof AudioContext; webkitAudioContext?: typeof AudioContext }).AudioContext
      || (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!Ctor) return false;
    if (!audioContext) audioContext = new Ctor();
    const ctx = audioContext;
    if (ctx.state === 'suspended') void ctx.resume().catch(() => undefined);
    const t0 = ctx.currentTime;
    for (const [freq, start, dur] of [[880, 0, 0.09], [1174.66, 0.09, 0.14]] as const) {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'sine';
      osc.frequency.value = freq;
      gain.gain.setValueAtTime(0.0001, t0 + start);
      gain.gain.exponentialRampToValueAtTime(0.06, t0 + start + 0.015);
      gain.gain.exponentialRampToValueAtTime(0.0001, t0 + start + dur);
      osc.connect(gain).connect(ctx.destination);
      osc.start(t0 + start);
      osc.stop(t0 + start + dur + 0.02);
    }
    lastChimeAt = now;
    return true;
  } catch {
    return false;
  }
}

// ─── Platform notifications ──────────────────────────────────────────────────

/**
 * Ask for platform-notification permission — called when the user CLICKS into
 * the 'chime-platform' state (a user gesture, which is when browsers allow
 * the prompt). Best-effort; 'denied' simply means the state behaves like
 * 'chime' until the user changes their browser setting.
 */
export function ensurePlatformNotificationPermission(): void {
  try {
    if (typeof Notification === 'undefined') return;
    if (Notification.permission === 'default') {
      void Notification.requestPermission().catch(() => undefined);
    }
  } catch { /* unsupported — chime still works */ }
}

/** Desktop browsers may draw from the tab; Android Chrome throws on `new Notification`, iOS has no tab path. */
function tabNotificationsAllowed(): boolean {
  try {
    const ua = typeof navigator !== 'undefined' ? navigator.userAgent : '';
    return !/Android|iPhone|iPad|iPod/i.test(ua);
  } catch {
    return false;
  }
}

/**
 * Show one platform notification for an incoming thread message.
 *
 * Draws through the messaging service worker's registration when it exists
 * (`registration.showNotification` — the only path Android Chrome accepts,
 * and the one an installed iOS app has); falls back to a tab-level
 * `new Notification` only on desktop browsers. Both carry the thread's deep
 * link: the worker's notificationclick focuses this window and posts
 * OPEN_THREAD; the tab fallback's onclick feeds the same live consumer
 * (lib/threadDeepLink). Returns true when a notification was requested.
 */
export function showThreadPlatformNotification(args: {
  /** Tag key: one live notification PER THREAD (2026-08-27 review fix — a
   * title-keyed tag made same-titled and untitled threads replace each
   * other's notifications). */
  threadId?: string;
  threadTitle: string;
  senderLabel?: string;
  preview?: string;
  /** The thread's workspace, for the deep link (absent → same-workspace open). */
  accountId?: string | null;
}): boolean {
  try {
    if (typeof Notification === 'undefined' || Notification.permission !== 'granted') return false;
    const title = (args.threadTitle || '').trim() || 'new message';
    const who = (args.senderLabel || '').trim();
    const preview = (args.preview || '').trim().slice(0, 140);
    const body = who && preview ? `${who}: ${preview}` : (preview || who || 'new message');
    const threadId = (args.threadId || '').trim();
    const tagKey = threadId || title;
    const url = threadId ? buildThreadDeepLinkUrl({ threadId, ws: args.accountId ?? null }) : '/';
    const options: NotificationOptions & { renotify?: boolean } = {
      body,
      icon: BRAND_NOTIFICATION_ICON,
      badge: BRAND_NOTIFICATION_BADGE,
      // tag: one live notification per thread — a burst REPLACES rather than stacks.
      tag: `thread-${tagKey}`,
      renotify: true,
      silent: true,
      data: { url, thread_id: threadId || null, account_id: args.accountId ?? null },
    };
    const drawFromTab = (): boolean => {
      if (!tabNotificationsAllowed()) return false;
      try {
        const n = new Notification(title, options);
        n.onclick = () => {
          try { window.focus(); } catch { /* noop */ }
          try { n.close(); } catch { /* noop */ }
          if (threadId) requestOpenThreadUrl(url);
        };
        return true;
      } catch {
        return false;
      }
    };
    if (typeof navigator !== 'undefined' && 'serviceWorker' in navigator) {
      void navigator.serviceWorker
        .getRegistration(MESSAGING_SW_SCOPE)
        .then((reg) => (reg ? reg.showNotification(title, options) : Promise.reject(new Error('no-registration'))))
        .catch(() => { drawFromTab(); });
      return true;
    }
    return drawFromTab();
  } catch {
    return false;
  }
}
